import React, { useCallback, useEffect, useMemo, useState } from 'react'

import env from '@/networkRequests/apiClient/env.config'
import { REDIRECT_TO_OFFERS_PAGE_TO_ADD_EXISTING_FLIGHT_KEY } from '@/pages/TripSummary/TripSummary.dictionary'
import { TripType } from '@/shared/functions/Flights/flights.types'
import localStorage from '@/shared/functions/LocalStorage/localStorage'
import {
  formatAirportPickupDisplay,
  formatMoney,
  joinStringsWithBullet
} from '@/shared/functions/String/string.functions'
import useResponsiveness from '@/shared/hooks/responsive.hooks'
import { FlightTakeoff, InfoOutlined, LocationOn, WarningAmber } from '@mui/icons-material'
import { Box, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material'

import Avatar from '../../../../shared/components/Avatar/Avatar.component'
import Button from '../../../../shared/components/Button/Button.component'
import USPSDeliveryMethodSelector from '../../../../shared/components/USPSDeliveryMethodSelector/USPSDeliveryMethodSelector.component'
import UploadFlightItineraryPDFModal from '../../../../shared/components/UploadFlightItineraryPDFModal/UploadFlightItineraryPDFModal.component'
import AcceptOffer from '../AcceptOffer/AcceptOffer.component'
import ItemDetails from '../ItemDetails/ItemDetails.component'

import { FlightItinerary as UploadFlightItinerary } from '../../../../redux/states/flights/externalFlightBookings/uploadPdf/uploadPdf.types'
import { FlightItinerary } from '../../../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.types'
import { getCountriesAndCities } from '../../../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import { clearFlightSearchResults } from '../../../../redux/states/flights/searchFlights/searchFlights.slice'
import { acceptFulfillmentOrder } from '../../../../redux/states/fulfillmentOrders/acceptFulfillmentOrder/acceptFulfillmentOrder.slice'
import { acceptPublicOffer } from '../../../../redux/states/offerRequest/acceptPublicOffer/acceptPublicOffer.slice'
import {
  FulfillmentOrder,
  OfferRequest
} from '../../../../redux/states/offerRequest/getPublicOffers/getPublicOffers.types'
import { AppDispatch, RootState } from '../../../../redux/store/store.types'
import router from '../../../../router/functions/router.functions'
import routes from '../../../../router/routes.dictionary'
import Modal from '../../../../shared/components/Modal/Modal.functions'
import Snackbar from '../../../../shared/components/Snackbar/Snackbar.functions'
import { isAuthenticated } from '../../../../shared/functions/Auth/auth.functions'
import date, { dateTimeFormats } from '../../../../shared/functions/Date/date.functions'
import { UserRole } from '../../../../shared/functions/UserRole/userRoleManagement.types'
import { getUserRole } from '../../../../shared/functions/UserRole/userRoleManagment.functions'
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/redux.hooks'
import { formatWeightLbs } from '../../../RecipientTracking/recipientTracking.functions'
import { getFormattedTripDetails } from '../../../Requests/Requests.functions'
import { SELECT_OFFER_SENDER_MODE_TOOLTIP, SHARED_OFFER_DETAILS_KEY } from '../../Offers.dictionary'
import { validateFulfillmentOrderAcceptance } from '../FulfillmentOrderCard/FulfillmentOrderCard.functions'
import { AcceptFulfillmentOrderValidationResult } from '../FulfillmentOrderCard/FulfillmentOrderCard.types'
import { validateOfferAcceptance } from './OfferItem.functions'
import styles from './OfferItem.styles'
import { AccepOfferValidationResult, OfferItemProps } from './OfferItem.types'

const OfferItem: React.FC<OfferItemProps> = (props) => {
  const {
    offer,
    flightItineraries,
    onSelect,
    isSelected,
    sharedOfferDetails,
    sharedFulfillmentOrderDetails,
    onAddMyFlight,
    orderIdAcceptedFromAddFlight
  } = props

  const dispatch = useAppDispatch()
  const navigate = router.navigate()

  const [isDesktop] = useResponsiveness()

  const [offerAccepted, setOfferAccepted] = useState(false)
  const [loadingOfferId, setLoadingOfferId] = useState<string | null>(null)
  const [isAddExistingFlightModalOpen, setIsAddExistingFlightModalOpen] = useState(false)
  const [acceptButtonPressed, setAcceptButtonPressed] = useState(false)

  const { loading: acceptPublicOfferLoading } = useAppSelector((state: RootState) => state.acceptPublicOffer)
  const { loading: acceptFulfillmentOrderLoading } = useAppSelector((state: RootState) => state.acceptFulfillmentOrder)
  const { loading: getAllFlightItinerariesLoading, success: getAllFlightItinerariesSuccess } = useAppSelector(
    (state: RootState) => state.getAllFlightItineraries
  )
  const countriesAndCitiesData = useAppSelector((state: RootState) => state.getCountriesAndCities.success?.data)
  const countriesAndCities = useMemo(() => countriesAndCitiesData || [], [countriesAndCitiesData])

  // Check if this is a FulfillmentOrder (must be declared before use)
  const isFulfillmentOrder = 'orderId' in offer
  const fulfillmentOrder = isFulfillmentOrder ? (offer as FulfillmentOrder) : null
  const offerRequest = !isFulfillmentOrder ? (offer as OfferRequest) : null

  const isSenderMode = getUserRole() === UserRole.sender

  // Load countries and cities data if not already loaded (needed for fulfillment order validation)
  useEffect(() => {
    if (isFulfillmentOrder && countriesAndCities.length === 0) {
      dispatch(getCountriesAndCities({}))
    }
  }, [dispatch, isFulfillmentOrder, countriesAndCities.length])

  const firstItem = offerRequest?.offerRequestDetails[0]?.itemDetails
  const currency = isFulfillmentOrder ? fulfillmentOrder?.currency : firstItem?.currency

  const handleSelect = () => {
    if (onSelect) {
      onSelect(offer)
    }
  }

  const onConfirmOffer = useCallback(
    (offer: OfferRequest, flightItinerary: FlightItinerary, dispatch: AppDispatch, onOfferAccepted: () => void) => {
      setLoadingOfferId(offer.offerRequestId.toString())
      dispatch(
        acceptPublicOffer({
          acceptPublicOfferRequest: {
            offerRequestId: offer.offerRequestId,
            confirmationFlightItineraryId: flightItinerary.id,
            isConfirming: true
          },
          onSuccess: () => {
            Snackbar.show({
              message: 'Offer successfully accepted! Go to the Requests page to see your accepted offers.',
              action: {
                label: 'Go to Requests',
                onClick: () => {
                  navigate(routes.requests.path)
                  window.scrollTo(0, 0)
                }
              },
              severity: 'success'
            })
            onOfferAccepted()
            setLoadingOfferId(null)
          }
        })
      )
    },
    [navigate]
  )

  const onConfirmFulfillmentOrder = useCallback(
    (
      order: FulfillmentOrder,
      flightItinerary: FlightItinerary,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      uspsDetails: any,
      applyToAll: boolean,
      dispatch: AppDispatch,
      onOrderAccepted: () => void
    ) => {
      setLoadingOfferId(order.orderId)
      dispatch(
        acceptFulfillmentOrder({
          acceptFulfillmentOrderRequest: {
            orderId: order.orderId,
            confirmationFlightItineraryId: flightItinerary.id,
            stayZipCode: uspsDetails?.stayZipCode,
            dropoffLocationId: uspsDetails?.dropoffLocationId,
            applyToAllOrdersOnFlight: applyToAll
          },
          onSuccess: () => {
            Snackbar.show({
              message: 'Fulfillment order successfully accepted! Go to the Requests page to see your accepted orders.',
              action: {
                label: 'Go to Requests',
                onClick: () => {
                  navigate(routes.requests.path)
                  window.scrollTo(0, 0)
                }
              },
              severity: 'success'
            })
            onOrderAccepted()
            setLoadingOfferId(null)
          }
        })
      )
    },
    [navigate]
  )

  const handleAddExistingFlightSubmit = (_values: UploadFlightItinerary) => {
    setIsAddExistingFlightModalOpen(false)
  }

  const onAccepOfferValidation = useCallback(():
    | AccepOfferValidationResult
    | AcceptFulfillmentOrderValidationResult => {
    if (isFulfillmentOrder && fulfillmentOrder) {
      const validation = validateFulfillmentOrderAcceptance(
        getAllFlightItinerariesSuccess?.data.results || flightItineraries,
        fulfillmentOrder,
        countriesAndCities
      )

      if (validation.noMatchingFlightItineraries) {
        const neededByDate = fulfillmentOrder.neededByDate
          ? `${date(fulfillmentOrder.neededByDate).format(dateTimeFormats.date.medium)}`
          : ''

        Modal.show({
          title: 'Book Flight',
          body: (
            <>
              <Typography variant="body1">
                You are required to have a flight leg from{' '}
                <span style={styles.linkText}>{validation.requiredCountries.itemLocation.name}</span> to{' '}
                <span style={styles.linkText}>
                  {validation.requiredCountries.deliveryLocation.city
                    ? `${validation.requiredCountries.deliveryLocation.city}, ${validation.requiredCountries.deliveryLocation.name}`
                    : validation.requiredCountries.deliveryLocation.name}
                </span>{' '}
                {neededByDate && (
                  <span>
                    arriving by <span style={styles.linkText}>{neededByDate}</span>
                  </span>
                )}{' '}
                to accept this order.
              </Typography>

              <Typography variant="body1" sx={styles.modalBodySpacing}>
                You can book a flight through {env.PRODUCT_NAME} or add one that you booked elsewhere.
              </Typography>
            </>
          ),
          primaryButton: {
            label: 'Book Flight',
            onClick: () => {
              dispatch(clearFlightSearchResults())

              navigate(routes.flightSearchResults.path, {
                state: {
                  requiredCountries: validation.requiredCountries,
                  selectedOrder: fulfillmentOrder,
                  selectedOffer: fulfillmentOrder // Add both for compatibility
                }
              })
            }
          },
          secondaryButton: {
            label: 'Add My Flight',
            onClick: () => {
              if (!isAuthenticated()) {
                if (sharedFulfillmentOrderDetails) {
                  localStorage.setItem(SHARED_OFFER_DETAILS_KEY, {
                    type: 'fulfillment_order',
                    orderId: sharedFulfillmentOrderDetails.orderId,
                    senderId: sharedFulfillmentOrderDetails.senderId
                  })
                }

                localStorage.setItem(REDIRECT_TO_OFFERS_PAGE_TO_ADD_EXISTING_FLIGHT_KEY, true)
                navigate(routes.login.path)

                return
              }

              if (onAddMyFlight) {
                onAddMyFlight(fulfillmentOrder)

                return
              }

              Modal.close()
              setTimeout(() => setIsAddExistingFlightModalOpen(true), 0)
            }
          },
          tertiaryButton: {
            label: 'Cancel'
          }
        })
      } else if (!validation.noMatchingFlightItineraries && !validation.multipleOptions) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let selectedUSPSDetails: any = null
        let applyToAllOrders = false

        Modal.show({
          title: 'Select Offer',
          subtitle: `This order will be assigned to the following itinerary:`,
          body: (
            <Box>
              <Typography variant="body1">
                {getFormattedTripDetails(
                  validation.flightItineraries[0].itinerary.legs,
                  validation.flightItineraries[0].itinerary.tripType as TripType
                )}

                {`\u00A0 (${
                  validation.flightItineraries[0].itinerary.legs.length > 1
                    ? `${date(validation.flightItineraries[0].itinerary.legs[0].travelDate).format(
                        dateTimeFormats.date.medium
                      )} - ${date(
                        validation.flightItineraries[0].itinerary.legs[
                          validation.flightItineraries[0].itinerary.legs.length - 1
                        ].travelDate
                      ).format(dateTimeFormats.date.medium)}`
                    : date(validation.flightItineraries[0].itinerary.legs[0].travelDate).format(
                        dateTimeFormats.date.medium
                      )
                })`}
              </Typography>

              {/* USPS Delivery Setup for USA destinations */}
              {fulfillmentOrder.destinationCountry === 'US' && (
                <Box sx={styles.modalUspsSection}>
                  <USPSDeliveryMethodSelector
                    onChange={(details) => {
                      selectedUSPSDetails = details
                    }}
                    optional={true}
                    title="USPS Delivery"
                    description="Choose how you'll deliver the package to the recipient"
                  />

                  {/* Checkbox to apply to all orders on this flight */}
                  {selectedUSPSDetails && (
                    <Box sx={styles.modalUspsCheckboxSection}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            defaultChecked={false}
                            onChange={(e) => {
                              applyToAllOrders = e.target.checked
                            }}
                          />
                        }
                        label={<Typography variant="body2">Use this setup for all packages on this flight</Typography>}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ),
          primaryButton: {
            label: 'Confirm',
            onClick: () => {
              onConfirmFulfillmentOrder(
                fulfillmentOrder,
                validation.flightItineraries[0],
                selectedUSPSDetails,
                applyToAllOrders,
                dispatch,
                () => setOfferAccepted(true)
              )
            },
            loading: acceptFulfillmentOrderLoading && loadingOfferId === fulfillmentOrder.orderId
          },
          secondaryButton: {
            label: 'Close'
          }
        })
      } else if (validation.multipleOptions) {
        let selectedFlightItineraryId: number | null = null
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let selectedUSPSDetails: any = null

        const handleConfirm = () => {
          if (selectedFlightItineraryId !== null) {
            const flightItinerary = validation.flightItineraries?.find(
              (flightItinerary) => flightItinerary.id === selectedFlightItineraryId
            )

            if (flightItinerary) {
              onConfirmFulfillmentOrder(fulfillmentOrder, flightItinerary, selectedUSPSDetails, false, dispatch, () =>
                setOfferAccepted(true)
              )
            } else {
              Snackbar.show({
                message: 'Please select a valid flight booking to accept the order.',
                severity: 'error'
              })
            }
          }
        }

        Modal.show({
          title: 'Choose Flight',
          subtitle: `You have multiple flight bookings available. Please choose one to accept the order.`,
          body: (
            <AcceptOffer
              response={{
                multipleOptions: validation.multipleOptions,
                noMatchingFlightItineraries: validation.noMatchingFlightItineraries,
                requiredCountries: {
                  itemLocation: validation.requiredCountries.itemLocation,
                  deliveryLocation: {
                    ...validation.requiredCountries.deliveryLocation,
                    neededByDate: validation.requiredCountries.deliveryLocation.neededByDate || ''
                  }
                },
                flightItineraries: validation.flightItineraries
              }}
              order={fulfillmentOrder}
              onConfirm={(flightItineraryId, uspsDetails) => {
                selectedFlightItineraryId = flightItineraryId
                selectedUSPSDetails = uspsDetails
              }}
            />
          ),
          primaryButton: {
            label: 'Confirm',
            onClick: handleConfirm
          },
          secondaryButton: {
            label: 'Close'
          }
        })
      }

      return validation
    }

    if (!offerRequest) {
      return {
        multipleOptions: false,
        noMatchingFlightItineraries: false,
        flightItineraries: [],
        requiredCountries: {
          itemLocation: { iataCode: '', name: '' },
          deliveryLocation: { iataCode: '', cityIataCode: '', name: '', city: '', neededByDate: null }
        }
      }
    }

    const items = offerRequest.offerRequestDetails.map((item) => item.itemDetails)
    const deliveryLocation = offerRequest.shopperDetails.deliveryTo
    const validation = validateOfferAcceptance(
      getAllFlightItinerariesSuccess?.data.results || flightItineraries,
      items,
      deliveryLocation,
      offerRequest
    )

    if (validation.noMatchingFlightItineraries) {
      const neededByDate = offerRequest.neededByDate
        ? `${date(offerRequest.neededByDate).format(dateTimeFormats.date.medium)}`
        : ''

      Modal.show({
        title: 'Book Flight',
        body: (
          <>
            <Typography variant="body1">
              You are required to have a flight leg from{' '}
              <span style={styles.linkText}>{validation.requiredCountries.itemLocation.name}</span> to{' '}
              <span style={styles.linkText}>
                {validation.requiredCountries.deliveryLocation.city
                  ? `${validation.requiredCountries.deliveryLocation.city}, ${validation.requiredCountries.deliveryLocation.name}`
                  : validation.requiredCountries.deliveryLocation.name}
              </span>{' '}
              {neededByDate && (
                <span>
                  arriving by <span style={styles.linkText}>{neededByDate}</span>
                </span>
              )}{' '}
              to accept this offer.
            </Typography>

            <Typography variant="body1" sx={styles.modalBodySpacing}>
              You can book a flight through {env.PRODUCT_NAME} or add one that you booked elsewhere.
            </Typography>
          </>
        ),
        primaryButton: {
          label: 'Book Flight',
          onClick: () => {
            dispatch(clearFlightSearchResults())
            navigate(routes.flightSearchResults.path, {
              state: {
                requiredCountries: validation.requiredCountries,
                selectedOffer: offerRequest
              }
            })
          }
        },
        secondaryButton: {
          label: 'Add My Flight',
          onClick: () => {
            if (!isAuthenticated()) {
              if (sharedOfferDetails) {
                localStorage.setItem(SHARED_OFFER_DETAILS_KEY, sharedOfferDetails)
              }

              localStorage.setItem(REDIRECT_TO_OFFERS_PAGE_TO_ADD_EXISTING_FLIGHT_KEY, true)
              navigate(routes.login.path)

              return
            }

            if (onAddMyFlight && offerRequest) {
              onAddMyFlight(offerRequest)

              return
            }

            Modal.close()
            setTimeout(() => setIsAddExistingFlightModalOpen(true), 0)
          }
        },
        tertiaryButton: {
          label: 'Cancel'
        }
      })
    } else if (!validation.noMatchingFlightItineraries && !validation.multipleOptions) {
      Modal.show({
        title: 'Select Offer',
        subtitle: `This offer will be assigned to the following itinerary:`,
        body: (
          <Box>
            <Typography variant="body1">
              {getFormattedTripDetails(
                validation.flightItineraries[0].itinerary.legs,
                validation.flightItineraries[0].itinerary.tripType as TripType
              )}

              {`\u00A0 (${
                validation.flightItineraries[0].itinerary.legs.length > 1
                  ? `${date(validation.flightItineraries[0].itinerary.legs[0].travelDate).format(
                      dateTimeFormats.date.medium
                    )} - ${date(
                      validation.flightItineraries[0].itinerary.legs[
                        validation.flightItineraries[0].itinerary.legs.length - 1
                      ].travelDate
                    ).format(dateTimeFormats.date.medium)}`
                  : date(validation.flightItineraries[0].itinerary.legs[0].travelDate).format(
                      dateTimeFormats.date.medium
                    )
              })`}
            </Typography>
          </Box>
        ),
        primaryButton: {
          label: 'Confirm',
          onClick: () => {
            if (offerRequest) {
              onConfirmOffer(offerRequest, validation.flightItineraries[0], dispatch, () => setOfferAccepted(true))
            }
          },
          loading: acceptPublicOfferLoading && loadingOfferId === offerRequest.offerRequestId.toString()
        },
        secondaryButton: {
          label: 'Close'
        }
      })
    } else if (validation.multipleOptions) {
      let selectedFlightItineraryId: number | null = null

      const handleConfirm = () => {
        if (selectedFlightItineraryId !== null) {
          const flightItinerary = validation.flightItineraries?.find(
            (flightItinerary) => flightItinerary.id === selectedFlightItineraryId
          )

          if (flightItinerary && offerRequest) {
            onConfirmOffer(offerRequest, flightItinerary, dispatch, () => setOfferAccepted(true))
          } else {
            Snackbar.show({
              message: 'Please select a valid flight booking to select the offer.',
              severity: 'error'
            })
          }
        }
      }

      Modal.show({
        title: 'Choose Flight',
        subtitle: `You have multiple flight bookings available. Please choose one to select the offer.`,
        body: (
          <AcceptOffer
            response={validation}
            onConfirm={(flightItineraryId) => {
              selectedFlightItineraryId = flightItineraryId
            }}
          />
        ),
        primaryButton: {
          label: 'Confirm',
          onClick: handleConfirm
        },
        secondaryButton: {
          label: 'Close'
        }
      })
    }

    return validation
  }, [
    dispatch,
    offerRequest,
    fulfillmentOrder,
    isFulfillmentOrder,
    navigate,
    flightItineraries,
    getAllFlightItinerariesSuccess,
    onConfirmOffer,
    onConfirmFulfillmentOrder,
    loadingOfferId,
    acceptPublicOfferLoading,
    acceptFulfillmentOrderLoading,
    sharedOfferDetails,
    sharedFulfillmentOrderDetails,
    countriesAndCities,
    onAddMyFlight
  ])

  useEffect(() => {
    if (acceptButtonPressed && !getAllFlightItinerariesLoading) {
      onAccepOfferValidation()
      setAcceptButtonPressed(false)
    }
  }, [acceptButtonPressed, getAllFlightItinerariesLoading, onAccepOfferValidation])

  // Render fulfillment order card
  if (isFulfillmentOrder && fulfillmentOrder) {
    const weightDisplay = formatWeightLbs(fulfillmentOrder.totalWeightGrams)

    // Extract origin city from airport location or lookup from airport IATA code
    // airportLocation format: "Logistics Partner Name" (e.g., "Distant Enterprises Ltd.")
    // Try to get city from airport IATA code lookup
    let originCity = ''

    if (fulfillmentOrder.airportIataCode && countriesAndCities.length > 0) {
      for (const country of countriesAndCities) {
        for (const city of country.cities) {
          const airport = city.airports.find((airport) => airport.iataCode === fulfillmentOrder.airportIataCode)

          if (airport) {
            originCity = `${city.cityName}, ${country.countryName}`
            break
          }
        }

        if (originCity) break
      }
    }

    // Fallback: try to parse from airportLocation if lookup didn't work
    if (!originCity && fulfillmentOrder.airportLocation) {
      const airportLocationParts = fulfillmentOrder.airportLocation.split(',').map((part) => part.trim())
      // If first part is IATA code (3 letters), skip it and use the rest
      if (airportLocationParts[0] && /^[A-Z]{3}$/.test(airportLocationParts[0])) {
        originCity = airportLocationParts.slice(1).join(', ') || airportLocationParts[0]
      } else {
        originCity = fulfillmentOrder.airportLocation
      }
    }

    // Final fallback
    if (!originCity) {
      originCity = fulfillmentOrder.airportIataCode || 'Airport Location'
    }

    const airportDetails = formatAirportPickupDisplay(fulfillmentOrder.airportName, fulfillmentOrder.airportLocation)

    const airportPickupTooltipText =
      'Pick up the items at the airport when you arrive. Once you arrive in the delivery country, you can ship the items via post to the recipient.'

    return (
      <Box sx={styles.offerContainer}>
        {/* Header with User Info, Traveler Benefit, and Order ID (matches offer request card layout) */}
        <Box sx={styles.topInfoContainer}>
          {isDesktop && fulfillmentOrder.senderDetails && (
            <Avatar
              firstName={fulfillmentOrder.senderDetails.firstName}
              lastName={fulfillmentOrder.senderDetails.lastName}
            />
          )}

          {fulfillmentOrder.senderDetails ? (
            <Box sx={styles.userContainer}>
              <Typography variant="h6" sx={styles.userName}>
                {`${fulfillmentOrder.senderDetails.firstName} ${fulfillmentOrder.senderDetails.lastName.charAt(0)}`}
              </Typography>

              <Typography variant="body2" sx={styles.userLocation}>
                {fulfillmentOrder.destinationCountry}
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.userContainer}>
              <Typography variant="h6" sx={styles.userName}>
                Fulfillment Order
              </Typography>

              <Typography variant="body2" sx={styles.userLocation}>
                {fulfillmentOrder.destinationCountry}
              </Typography>
            </Box>
          )}

          <Typography variant="h6" sx={styles.getAmount}>
            Get {formatMoney(fulfillmentOrder.travelerBenefit ?? 0, fulfillmentOrder.currency)}
          </Typography>
        </Box>

        {/* Order Details Section */}
        <Box sx={styles.fulfillmentItemSection}>
          <Box sx={styles.fulfillmentItemSectionInner}>
            {/* First Item Image */}
            {fulfillmentOrder.imageUrls && fulfillmentOrder.imageUrls.length > 0 && (
              <Box sx={styles.fulfillmentItemImageContainer}>
                <img src={fulfillmentOrder.imageUrls[0]} alt="Order items" style={styles.fulfillmentItemImage} />
              </Box>
            )}

            {/* Order Details */}
            <Box sx={styles.fulfillmentItemDetails}>
              <Box sx={styles.fulfillmentItemTitleRow}>
                <Typography variant="h6" sx={styles.fulfillmentItemTitle}>
                  {fulfillmentOrder.itemDescription || 'Order Items'}
                </Typography>

                {fulfillmentOrder.requiresSpecialHandling && (
                  <Tooltip
                    title="Special handling required for this order. Extra care is needed when packing and transporting the items."
                    arrow
                    placement="top"
                  >
                    <WarningAmber sx={styles.specialHandlingIcon} />
                  </Tooltip>
                )}
              </Box>

              <Typography variant="body2" sx={styles.fulfillmentItemMeta}>
                {joinStringsWithBullet([
                  `Qty: ${fulfillmentOrder.itemCount}`,
                  fulfillmentOrder.totalWeightGrams != null && fulfillmentOrder.totalWeightGrams > 0
                    ? `Weighing: ${weightDisplay}`
                    : 'Weighing: N/A'
                ])}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Location Info */}
        <Box sx={styles.bottomInfoContainer}>
          <Box sx={styles.locationInfoContainer}>
            <LocationOn sx={styles.locationIcon} />

            <Typography variant="body2">
              <span style={styles.boldText}>{originCity}</span> <span style={styles.lightText}>to</span>{' '}
              <span style={styles.boldText}>{fulfillmentOrder.destinationCountry}</span>
            </Typography>
          </Box>

          <Box sx={styles.neededByContainer}>
            <Box sx={styles.pickupInfoRow}>
              <FlightTakeoff sx={styles.pickupInfoIcon} />

              <Typography variant="body2" sx={styles.pickupInfoText}>
                <span style={styles.lightText}>Pickup at:</span> <span style={styles.boldText}>{airportDetails}</span>
              </Typography>

              <Tooltip title={airportPickupTooltipText} arrow placement="top">
                <InfoOutlined sx={styles.pickupInfoTooltipIcon} />
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Select Offer Button */}
        {onSelect ? (
          <Button
            text={isSelected ? 'Deselect' : 'Select Offer'}
            fullWidth
            variant={isSelected ? 'contained' : 'outlined'}
            buttonType={isSelected ? 'tertiary' : 'primary'}
            onClick={handleSelect}
            disabled={isSenderMode || fulfillmentOrder.isCurrentUserSender}
            tooltipText={
              isSenderMode
                ? SELECT_OFFER_SENDER_MODE_TOOLTIP
                : fulfillmentOrder.isCurrentUserSender
                  ? 'You are not allowed to select your own order.'
                  : undefined
            }
          />
        ) : (
          <Button
            text={
              offerAccepted || orderIdAcceptedFromAddFlight === fulfillmentOrder.orderId
                ? 'Order Accepted'
                : 'Select Offer'
            }
            fullWidth
            disabled={
              offerAccepted ||
              orderIdAcceptedFromAddFlight === fulfillmentOrder.orderId ||
              isSenderMode ||
              fulfillmentOrder.isCurrentUserSender
            }
            buttonType="primary"
            onClick={() => setAcceptButtonPressed(true)}
            tooltipText={
              isSenderMode
                ? SELECT_OFFER_SENDER_MODE_TOOLTIP
                : fulfillmentOrder.isCurrentUserSender
                  ? 'You are not allowed to select your own order.'
                  : undefined
            }
            loading={
              (acceptFulfillmentOrderLoading && loadingOfferId === fulfillmentOrder.orderId) ||
              (getAllFlightItinerariesLoading && acceptButtonPressed)
            }
          />
        )}
      </Box>
    )
  }

  // Render regular offer request card
  if (!offerRequest) return null

  return (
    <Box sx={styles.offerContainer}>
      <Box sx={styles.topInfoContainer}>
        {isDesktop && (
          <Avatar firstName={offerRequest.shopperDetails.firstName} lastName={offerRequest.shopperDetails.lastName} />
        )}

        <Box sx={styles.userContainer}>
          <Typography variant="h6" sx={styles.userName}>
            {`${offerRequest.shopperDetails.firstName} ${offerRequest.shopperDetails.lastName.charAt(0)}`}
          </Typography>

          <Typography variant="body2" sx={styles.userLocation}>
            {`${offerRequest.shopperDetails.deliveryTo.city}, ${offerRequest.shopperDetails.deliveryTo.country}`}
          </Typography>
        </Box>

        <Typography variant="h6" sx={styles.getAmount}>
          Get {formatMoney(offerRequest.travelerBenefit, currency || 'USD')}
        </Typography>
      </Box>

      <Box sx={styles.itemDetailsContainer}>
        {offerRequest.offerRequestDetails.map((detail, index) => (
          <ItemDetails key={index} item={detail.itemDetails} />
        ))}
      </Box>

      <Box sx={styles.bottomInfoContainer}>
        <Box sx={styles.locationInfoContainer}>
          <LocationOn sx={styles.locationIcon} />

          <Typography variant="body2">
            <span style={styles.boldText}>
              {[...new Set(offerRequest.offerRequestDetails.map((detail) => detail.itemDetails.country.name))].join(
                ', '
              )}
            </span>{' '}
            <span style={styles.lightText}>to</span>{' '}
            <span
              style={styles.boldText}
            >{`${offerRequest.shopperDetails.deliveryTo.city}, ${offerRequest.shopperDetails.deliveryTo.country}`}</span>
          </Typography>
        </Box>

        <Box sx={styles.neededByContainer}>
          <Typography variant="body2">
            <span style={styles.lightText}>Needed by:</span>{' '}
            <span style={styles.boldText}>
              {offerRequest.neededByDate === null
                ? 'Anytime'
                : date(offerRequest.neededByDate).format(dateTimeFormats.date.medium)}
            </span>
          </Typography>
        </Box>
      </Box>

      {onSelect ? (
        <Button
          text={isSelected ? 'Deselect' : 'Select Offer'}
          fullWidth
          variant={isSelected ? 'contained' : 'outlined'}
          buttonType={isSelected ? 'tertiary' : 'primary'}
          onClick={handleSelect}
          disabled={offerRequest.isCurrentUserShopper || isSenderMode}
          tooltipText={
            isSenderMode
              ? SELECT_OFFER_SENDER_MODE_TOOLTIP
              : offerRequest.isCurrentUserShopper
                ? 'You are not allowed to select your own offer.'
                : undefined
          }
        />
      ) : (
        <Button
          text={offerAccepted ? 'Offer Selected' : 'Select Offer'}
          tooltipText={
            isSenderMode
              ? SELECT_OFFER_SENDER_MODE_TOOLTIP
              : offerRequest.isCurrentUserShopper
                ? 'You are not allowed to select your own offer.'
                : undefined
          }
          fullWidth
          disabled={offerAccepted || offerRequest.isCurrentUserShopper || isSenderMode}
          buttonType="primary"
          onClick={() => setAcceptButtonPressed(true)}
          loading={
            (acceptPublicOfferLoading && loadingOfferId === offerRequest.offerRequestId.toString()) ||
            (getAllFlightItinerariesLoading && acceptButtonPressed)
          }
        />
      )}

      {isAddExistingFlightModalOpen && (
        <UploadFlightItineraryPDFModal
          open={isAddExistingFlightModalOpen}
          onClose={() => setIsAddExistingFlightModalOpen(false)}
          onSubmit={handleAddExistingFlightSubmit}
          offerRequest={offerRequest ? { offerRequestId: offerRequest.offerRequestId.toString() } : undefined}
          fulfillmentOrder={fulfillmentOrder ? { orderId: fulfillmentOrder.orderId } : undefined}
        />
      )}
    </Box>
  )
}

export default OfferItem
